import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApi } from '../../Services/apiService';
import { AdminBadge, statusToBadge } from '../components/AdminUi';
import Button from '../../Components/Button/Button';
import { getPropertyListingStatusLabel } from '../../constants/propertyListingStatus';

const PROPERTY_TYPE_LABELS = {
  '1': 'Apartment',
  '2': 'House',
  '3': 'Townhouse',
  '4': 'Studio',
};

function getPropertyTypeLabel(type) {
  return PROPERTY_TYPE_LABELS[String(type)] || type || '—';
}

function formatCurrency(amount) {
  if (amount == null || amount === '') return '—';
  return `$${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

export default function LandlordPropertiesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [landlord, setLandlord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchLandlord = async () => {
      setIsLoading(true);
      try {
        const response = await getApi(`/admin/landlords/${id}`);
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          setLandlord(response.data);
        } else {
          toast.error(response?.message || 'Failed to load landlord properties');
          navigate('/admin/dashboard/landlords', { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load landlord properties';
          toast.error(message);
          navigate('/admin/dashboard/landlords', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    if (id) {
      fetchLandlord();
    }

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const properties = landlord?.properties ?? [];

  return (
    <div className="admin-page admin-page--list">
      <div className="admin-toolbar" style={{ marginBottom: 16 }}>
        <Button size="small" variant="outline" onClick={() => navigate('/admin/dashboard/landlords')}>
          ← Back to Landlords
        </Button>
      </div>

      {!isLoading && landlord && (
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 600 }}>{landlord.name}</h2>
          <p style={{ margin: 0, fontSize: '14px', color: '#7c7e93' }}>
            {properties.length} {properties.length === 1 ? 'property' : 'properties'}
          </p>
        </div>
      )}

      <div className="admin-table-wrap">
        <div className="admin-table-scroll">
        <table className="admin-table admin-table--compact">
          <thead>
            <tr>
              <th className="admin-table-sn">S.No.</th>
              <th>Code</th>
              <th>Type</th>
              <th>Unit</th>
              <th>City</th>
              <th>State</th>
              <th>Zip</th>
              <th>Monthly Rent</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="admin-table-empty">
                  Loading properties...
                </td>
              </tr>
            ) : properties.length === 0 ? (
              <tr>
                <td colSpan={9} className="admin-table-empty">
                  No properties listed for this landlord.
                </td>
              </tr>
            ) : (
              properties.map((property, index) => (
                <tr key={property.id}>
                  <td className="admin-table-sn">{index + 1}</td>
                  <td style={{ fontWeight: 600 }}>{property.property_code || '—'}</td>
                  <td>{getPropertyTypeLabel(property.property_type)}</td>
                  <td>{property.unit_number || '—'}</td>
                  <td>{property.city || '—'}</td>
                  <td>{property.state || '—'}</td>
                  <td>{property.zip_code || '—'}</td>
                  <td style={{ fontWeight: 600 }}>{formatCurrency(property.monthly_rent)}</td>
                  <td>
                    <AdminBadge variant={statusToBadge(getPropertyListingStatusLabel(property.status))}>
                      {getPropertyListingStatusLabel(property.status)}
                    </AdminBadge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
